/* eslint-disable consistent-return */
import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  FC,
  useEffect,
} from 'react';

import {
  fetchAllAutoApprovals,
  fetchCoursesWithValidation,
  createAutoApprovalService,
  createCourseRelationship,
  fetchAutoApprovalDetail,
  fetchBolsasMaisAcesso,
  deleteCourseRelationship,
  deleteAutoApproval,
  updateAutoApprovalStatus,
  updateCourseData,
  updateAutoApprovalData,
} from 'services/AutoApprovalService/requests';
import type {
  CreateAutoApprovalParams,
  UpdateAutoApprovalParams,
  UpdateCourseDataParams,
} from 'models/auto-approval';

export interface AutoApprovalDTO {
  bolsa?: string;
  cursos?: [];
  id: number;
  nome: string;
  periodo: string;
  prazoFinal: string;
  prazoInicial: string;
  status: string;
  tipo: string;
}

export interface CreateAutoApprovalRelationshipParams {
  aprovacaoAutomaticaId: number;
  cursoId: number;
  turno: string;
  campus: string;
  nome: string;
}

export interface CourseTableListDTO {
  id: number;
  cursoId: number;
  limiteVagas: string;
  vagas: number;
  mensalidade: number;
  nome: string;
  turno: string;
  percentual: number;
  campus: string;
}

export interface CourseTableListInterface {
  cursos: CourseTableListDTO[];
}

export interface CourseListDTO {
  conflito: boolean;
  curso: string;
  id: number;
  nomeCampus: string;
  turno: string;
  mensalidade: number;
}

interface GetAllCoursesListProps {
  bolsaID: number;
  aprovacaoID: number;
}

interface AutoApprovalData {
  data: AutoApprovalDTO[];
  isLoading: boolean;
  error: Error | null;
  clearError: () => void;
  getAllAutoApprovals: () => Promise<void>;
  getAllCoursesList: (params: GetAllCoursesListProps) => Promise<void>;
  coursesList: CourseListDTO[];
  createAutoApproval: (params: CreateAutoApprovalParams) => Promise<any>;
  currentFormData: CurrentFormDataDTO;
  createAutoApprovalRelationship: any;
  getCurrentAutoApproval: (id: number) => Promise<void>;
  coursesTableList: CourseTableListDTO[];
  bolsasMaisAcesso: BolsaMaisAcessoDTO[];
  removeTableItem: (id: number) => Promise<void>;
  removeAutoApproval: (id: number) => Promise<void>;
  updateStatusAutoApproval: (id: number) => Promise<void>;
  getCurrentDraftAutoApproval: (id: number) => Promise<void>;
  resetCurrentFormData: () => void;
  editCourseData: (data: UpdateCourseDataParams) => Promise<void>;
  editAutoApprovalData: (params: UpdateAutoApprovalParams) => Promise<void>;
  setCurrentFormData: React.Dispatch<React.SetStateAction<CurrentFormDataDTO>>;
}

interface CurrentFormDataDTO {
  aprovacaoId?: number;
  aprovacaoNome?: string;
  tipo?: string;
  prazoInicial?: Date;
  prazoFinal?: Date;
  periodo?: string;
  status?: string;
  cursos?: any[];
  bolsa?: number;
}

interface BolsaMaisAcessoDTO {
  id: number;
  nome: string;
  codigo: number;
  solucao: string;
  sigla: string;
}

const AutoApprovalContext = createContext<AutoApprovalData>(
  {} as AutoApprovalData,
);

const INITIAL_STATE: CurrentFormDataDTO = {
  aprovacaoId: undefined,
  aprovacaoNome: '',
  tipo: '',
  prazoInicial: undefined,
  prazoFinal: undefined,
  periodo: '',
  status: undefined,
  cursos: [],
  bolsa: undefined,
};

const AutoApprovalProvider: FC = ({ children }) => {
  const [data, setData] = useState<AutoApprovalDTO[]>([]);
  const [currentFormData, setCurrentFormData] = useState<CurrentFormDataDTO>(
    INITIAL_STATE,
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [coursesList, setCoursesList] = useState<CourseListDTO[]>([]);
  const [coursesTableList, setCoursesTableList] = useState<any>([]);
  const [bolsasMaisAcesso, setBolsasMaisAcesso] = useState<
    BolsaMaisAcessoDTO[]
  >([]);

  const getAllAutoApprovals = useCallback(async () => {
    const response = await fetchAllAutoApprovals();
    setData(response.data.aprovacoesAutomatizadas);
  }, []);

  const getAllCoursesList = useCallback(
    async ({ aprovacaoID, bolsaID }: GetAllCoursesListProps) => {
      const response = await fetchCoursesWithValidation({
        aprovacaoID,
        bolsaID,
      });

      setCoursesList(response.data.cursos);
    },
    [],
  );

  const initRequests = useCallback(async () => {
    try {
      setIsLoading(true);
      const responses = await Promise.all<any>([
        fetchAllAutoApprovals(),
        fetchBolsasMaisAcesso(),
      ]);

      setData(responses[0].data.aprovacoesAutomatizadas);
      setBolsasMaisAcesso(responses[1].data.bolsas);

      setIsLoading(false);
    } catch (err) {
      setError(err);
      setIsLoading(false);
    }
  }, []);

  const createAutoApproval = useCallback(
    async (params: CreateAutoApprovalParams): Promise<any> => {
      try {
        setIsLoading(true);
        const response = await createAutoApprovalService(params);

        setCurrentFormData((currState) => ({
          ...currState,
          aprovacaoId: response.data.id,
          aprovacaoNome: response.data.nome,
          tipo: response.data.tipo,
          status: response.data.status,
          periodo: response.data.periodo,
        }));

        setIsLoading(false);

        return response.data;
      } catch (err) {
        setIsLoading(false);
        setError(err);
      }
    },
    [],
  );

  const createAutoApprovalRelationship = useCallback(
    async (coursesArray: CreateAutoApprovalRelationshipParams[]) => {
      try {
        setIsLoading(true);
        const response = await createCourseRelationship(coursesArray);

        setIsLoading(false);
        return response;
      } catch (err) {
        setIsLoading(false);
        setError(err);
      }
    },
    [currentFormData],
  );

  const getCurrentAutoApproval = useCallback(
    async (id: number): Promise<any> => {
      try {
        setIsLoading(true);
        const response = await fetchAutoApprovalDetail(id);
        setCoursesTableList(response.data.cursos);
        setCurrentFormData((currState) => ({
          ...currState,
          periodo: response.data.periodo,
          aprovacaoNome: response.data.nome,
          prazoFinal: response.data.prazoFinal,
          prazoInicial: response.data.prazoInicial,
          status: response.data.status,
          tipo: response.data.tipo,
        }));
        setIsLoading(false);

        return response;
      } catch (err) {
        setIsLoading(false);
        setError(err);
      }
    },
    [currentFormData],
  );

  const getCurrentDraftAutoApproval = useCallback(
    async (approvalId: number): Promise<void> => {
      try {
        setIsLoading(true);
        const response = await fetchAutoApprovalDetail(approvalId);

        const {
          id,
          nome,
          bolsa,
          periodo,
          prazoFinal,
          prazoInicial,
          status,
          tipo,
          cursos,
        } = response.data;

        if (cursos) {
          await getAllCoursesList({ aprovacaoID: id, bolsaID: bolsa });
          setCoursesTableList(cursos);
        }

        setCurrentFormData({
          aprovacaoId: id,
          aprovacaoNome: nome,
          periodo,
          tipo,
          status,
          prazoFinal,
          prazoInicial,
          bolsa,
        });

        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        setError(err);
      }
    },
    [],
  );

  const removeTableItem = useCallback(
    async (id: number): Promise<void> => {
      try {
        setIsLoading(true);
        await deleteCourseRelationship(id);
        if (currentFormData.aprovacaoId && currentFormData?.bolsa) {
          await getCurrentAutoApproval(currentFormData.aprovacaoId);
          await getAllCoursesList({
            aprovacaoID: currentFormData.aprovacaoId,
            bolsaID: currentFormData.bolsa,
          });
        }
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        setError(err);
      }
    },
    [currentFormData],
  );

  const removeAutoApproval = useCallback(async (id: number): Promise<void> => {
    try {
      setIsLoading(true);
      await deleteAutoApproval(id);
      initRequests();
    } catch (err) {
      setIsLoading(false);
      setError(err);
    }
  }, []);

  const updateStatusAutoApproval = useCallback(async (id: number): Promise<
    void
  > => {
    try {
      setIsLoading(true);
      await updateAutoApprovalStatus(id);
      initRequests();
    } catch (err) {
      setIsLoading(false);
      setError(err);
    }
  }, []);

  const resetCurrentFormData = useCallback(() => {
    setCurrentFormData(INITIAL_STATE);
    setCoursesTableList([]);
    setCoursesList([]);
  }, []);

  const editCourseData = useCallback(async (params: UpdateCourseDataParams) => {
    try {
      setIsLoading(true);

      await updateCourseData(params);
      // await getCurrentAutoApproval(currentFormData.aprovacaoId || 0);

      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      setError(err);
    }
  }, []);

  const editAutoApprovalData = useCallback(
    async (params: UpdateAutoApprovalParams) => {
      try {
        setIsLoading(true);

        await updateAutoApprovalData(params);
        await getAllCoursesList({
          aprovacaoID: params.id,
          bolsaID: currentFormData.bolsa || 0,
        });
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        setError(err);
      }
    },
    [currentFormData],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, [error]);

  useEffect(() => {
    initRequests();
  }, []);

  return (
    <AutoApprovalContext.Provider
      value={{
        data,
        setCurrentFormData,
        clearError,
        error,
        getAllAutoApprovals,
        isLoading,
        coursesList,
        getAllCoursesList,
        createAutoApproval,
        currentFormData,
        createAutoApprovalRelationship,
        getCurrentAutoApproval,
        coursesTableList,
        bolsasMaisAcesso,
        removeTableItem,
        removeAutoApproval,
        updateStatusAutoApproval,
        getCurrentDraftAutoApproval,
        resetCurrentFormData,
        editCourseData,
        editAutoApprovalData,
      }}
    >
      {children}
    </AutoApprovalContext.Provider>
  );
};

const useAutoApproval = (): AutoApprovalData => {
  const context = useContext(AutoApprovalContext);

  if (!context) {
    throw new Error('useAutoApproval should be used inside auth context');
  }

  return context;
};

export { AutoApprovalProvider, useAutoApproval };
